<template>
	<main :data-test="$route.path">
		<header>
			<pageHeader1image v-bind="pageHeader">
				<button class="button button-goPink button-large space-x-12 group shadow-lg" @click.prevent="$scrollTo('our-mission')">
					<span> What we're about </span>
					<svg-icon name="arrow-forward" class="iconSize-24 opacity-50 translate-x-0 group-hover:translate-x-1" />
				</button>
			</pageHeader1image>

			<aboutUsHeader />

			<hr class="m-0 tablet:ml-48 desktop:ml-56 border-black/10" />
		</header>

		<div class="py-16 tablet:py-48 desktop:py-56 space-y-56 tabletWide:space-y-112">
			<LazyHydrate when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<header scroll-target="our-mission">
							<p class="font-bold text-20 tablet:text-24">Our promise to you</p>
							<h2 class="font-bold text-32 desktop:text-48 text-goPink">To be there with you every step <span class="whitespace-nowrap">of the way</span></h2>
						</header>

						<p class="text-18 desktop:text-22 text-goGrayDarker">Let's be honest, most companies put a whole lot of stuff on their 'mission page' to talk about how great they are. We prefer action over talk so you can make up your own mind.</p>
						<p>We like to believe that GO is radically changing the way rental car companies work.</p>
						<p>We know other car rental companies can make booking a car difficult, so we've gone above and beyond to simplify the process. We'll take care of the important stuff leaving you the freedom to relax and enjoy the ride in your car hire. This is all so you can get on the road faster, without all the hassles, and GO love every moment of your holiday.</p>

						<header>
							<h3 class="font-bold text-20 tablet:text-24">Why we're different</h3>
						</header>

						<ul class="text-14 space-y-16 desktop:text-16">
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Outstanding on-road support</span> <br />
								We've got your back - so you can make the most of your time.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Ridiculously simple pick-up and drop-off</span> <br />
								With our online check-in and app, you'll be through pick up and on the road faster. With our excellent customer service, we personalise the drop off service so you can be on your way to your next location.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">High Quality App</span> <br />
								One of our aims is to innovate the car rental industry and we are always updating and expanding our <nuxt-link to="/app/" class="link link-goBlue font-bold">GO App</nuxt-link>. You can book an airport shuttle, rent a car, track and control your Tesla and much more.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Fair & transparent car rentals</span> <br />
								We've radically changed how car rental companies handle paperwork. Our policies are designed to be hassle free for you.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Quality vehicles just right for you</span> <br />
								A wide selection of new vehicles perfect for your getaway.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Luxurious Tesla Vehicles</span> <br />
								<nuxt-link to="/tesla-rental-nz/" class="link link-goBlue font-bold">Electric vehicles</nuxt-link> are not only deluxe and smooth to drive, they give our customers the full confidence that their car rental is environmentally friendly.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Climate Friendly Partnerships</span> <br />
								GO Rentals are committed to keeping their carbon neutral status and promise. We have partnered with CarbonClick so our customers can also <nuxt-link to="/go-zero/" class="link link-goBlue font-bold">offset their fuel emissions</nuxt-link> to reduce the environmental impact of travel.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Trip planner, guides and travel blog</span> <br />
								We understand that it is more than a car rental, it is about adventures, quality time and making memories. So we have designed lots of tools to take the stress out of planning and provide you with local knowledge.
							</li>
							<li class="bg-iconChevron pl-32">
								<span class="font-bold">Exclusive Discounts</span> <br />
								We understand that sometimes we have a tight budget and need to save money, so we regularly release <nuxt-link to="/deals/" class="link link-goBlue font-bold">affordable deals</nuxt-link> and <nuxt-link to="/rental-car-relocation/" class="link link-goBlue font-bold">relocations</nuxt-link> so you can get the best price on your car rental.
							</li>
						</ul>
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection v-if="$store.state.reviews.reviews.length">
					<layoutPost type="wide">
						<article class="space-y-32">
							<header>
								<h2 class="font-bold text-20 tablet:text-24">Reviews and Awards</h2>
							</header>

							<div class="space-y-16">
								<p>We are one of the top rated companies in the New Zealand customer service and hire car industry.</p>
							</div>
						</article>
					</layoutPost>

					<div class="mt-32 p-16 tablet:p-0 grid grid-cols-12 items-center tablet:items-stretch tabletWide:gap-16">
						<allReviews class=" col-span-8 tablet:col-span-10 tablet:col-span-9 desktop:col-span-6"/>
						<allEndorsements class=" h-full border-black/10 border-l col-span-4 tablet:col-span-2 tablet:col-span-3 desktop:col-span-6"/>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<videoYouTube
						title="The GO Experience"
						src="https://www.youtube.com/embed/KjChqxbIHoY"
						duration="1:11"
						ratio="3:2"
						:image="{
							mobile: { src: '/images/About_branch.jpg' },
							alt: 'Interior image of GO Rentals\' Auckland Airport branch',
						}"
					 />
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection type="post2">
					<template #main>
						<layoutPost type="left">
							<header>
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">We make it personal</h2>
							</header>

							<p class="text-18 desktop:text-22">We're a big team of extremely passionate people who honestly love the jobs we do. Whether you’re taking a quick day trip, or maybe taking the family on an adventure around the country, we'll let you know how important you are to us. Our priority is helping you enjoy every moment of your time on the road.</p>

							<ul class="text-14 space-y-4 desktop:text-16">
								<li class="bg-iconChevron pl-32">We love New Zealand and want to share that passion and local knowledge with our customers. This is why we have designed a <a :href="$config.goExploreBaseURL + 'trip-planner/'" class="link link-goBlue font-bold">trip planner</a> tool so we can help you make the most of your road trip itinerary.</li>
								<li class="bg-iconChevron pl-32">Customers can check out our fun and exciting <a :href="$config.goExploreBaseURL + 'blog/'" class="link link-goBlue font-bold">travel blog</a> for tips and inspiration. Covering all our locations you can rent a car in and everywhere in between, we want our GO rentals car to help you have the best holiday.</li>
								<li class="bg-iconChevron pl-32">We love to see the happy memories in our GO rental cars! Customers can submit a travel photo to our <nuxt-link to="/go-snap-happy/" class="link link-goBlue font-bold">Snap Happy competition</nuxt-link> and be in with a chance to win up to $500 of their rental car cost back.</li>
							</ul>

							<footer>
								<nuxt-Link to="/about-us/reviews/" class="button button-goBlueGhost button-normal">
									Customer reviews
								</nuxt-Link>
							</footer>
						</layoutPost>
					</template>

					<template #aside_left>
						<goPicture
							class="w-full h-full"
							alt="Image of a member of the GO Rentals Team preparing a rental car for a new customer"
							:mobile="{ src: '/images/About_Customer_1.jpg', width: 184, height: 276 }"
							:tablet="{ src: '/images/About_Customer_1.jpg', width: 328, height: 492 }"
							:tabletWide="{ src: '/images/About_Customer_1.jpg', width: 220, height: 330 }"
							:desktop="{ src: '/images/About_Customer_1.jpg', width: 280, height: 420 }"
							:desktopWide="{ src: '/images/About_Customer_1.jpg', width: 320, height: 480 }"
						/>
					</template>

					<template #aside_right>
						<goPicture
							class="w-full h-full overflow-hidden rounded-GO"
							alt="Aerial image of a road in the South Island with a lake and mountain in the background"
							:mobile="{ src: '/images/About_Customer_2.jpg', width: 184, height: 276 }"
							:tablet="{ src: '/images/About_Customer_2.jpg', width: 328, height: 492 }"
							:tabletWide="{ src: '/images/About_Customer_2.jpg', width: 220, height: 330 }"
							:desktop="{ src: '/images/About_Customer_2.jpg', width: 280, height: 420 }"
							:desktopWide="{ src: '/images/About_Customer_2.jpg', width: 320, height: 480 }"
						/>
					</template>

					<template #post-below>
						<div class=" col-span-12 p-16 tablet:p-0 tablet:absolute tablet:pointer-events-none tablet:z-10 tablet:inset-0 tablet:bottom-[50%] tabletWide:bottom-0 tablet:flex tablet:items-center tablet:justify-center ">
							<feedback
								class="tabletWide:translate-x-4/12 bg-goGrayBrighter tablet:bg-white/80 tablet:shadow-sm tablet:max-w-342 tablet:pointer-events-auto"
								text="We were highly impressed by both the car & the high standard of service & support. I felt safe driving my daughter & I around in it as it was very dependable in whatever road conditions there were."
								avatar="/images/About_Annette.jpg"
								alt="Image of Annette from Australia next to review of GO Rentals"
								name="Annette"
								detail="Australia"
								/>
						</div>
					</template>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection type="post-reversed">
					<template #main>
						<layoutPost type="center">
							<header>
								<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">We've been around the block</h2>
							</header>

							<p class="text-18 desktop:text-22">When GO Rentals was born way back in 1997, we were a small family business. Over the last 20 years the 'GO family' has grown to over 200 scattered around the country and even the globe! Each and every day, these are the people who make GO the special place it is and allow us to offer you a unique GO way of doing business.</p>

							<p>Every team member, whether they are in the head office or one of our car hire locations, works towards the same values:</p>
							<p>Every team member, whether they are in the head office or one of our car hire locations, works towards the same values:</p>

							<ul class="text-14 space-y-4 desktop:text-16">
								<li class="bg-iconChevron pl-32">Mana Tangata (respecting each other)</li>
								<li class="bg-iconChevron pl-32">Whanaungatanga (working together)</li>
								<li class="bg-iconChevron pl-32">Be innovative</li>
								<li class="bg-iconChevron pl-32">Achieving excellence</li>
								<li class="bg-iconChevron pl-32">Service first</li>
							</ul>

							<footer>
								<nuxt-Link to="/about-us/our-team/" class="button button-goBlueGhost button-normal">
									Meet our team
								</nuxt-Link>
							</footer>
						</layoutPost>
					</template>

					<template #aside>
						<goPicture
							class="w-full h-full"
							alt="Image of a Toyota Rav4 at dusk parked up next to a tent with mountains in background"
							:mobile="{ src: '/images/About_Team.jpg', width: 375, height: 250 }"
							:tablet="{ src: '/images/About_Team.jpg', width: 456, height: 304 }"
							:tabletWide="{ src: '/images/About_Team.jpg', width: 220, height: 330 }"
							:desktop="{ src: '/images/About_Team.jpg', width: 576, height: 384 }"
							:desktopWide="{ src: '/images/About_Team.jpg', width: 656, height: 438 }"
						/>
					</template>
				</layoutSection>
			</LazyHydrate>
		</div>
	</main>
</template>

<script>
import aboutUsHeader from '~/components/aboutUsHeader.vue';
import layoutSection from '~/components/layoutSection.vue';
import layoutPost from '~/components/layoutPost.vue';
import feedback from '~/components/feedback.vue';

export default {
	components: {
		aboutUsHeader,
		layoutSection,
		layoutPost,
		feedback,
		pageHeader1image: () => import("~/components/pageHeader1image.vue"),
		videoYouTube: () => import("~/components/videoYouTube.vue"),
		allReviews: () => import(/* webpackChunkName: "allReviews" */ "~/components/allReviews.vue"),
		allEndorsements: () => import(/* webpackChunkName: "allEndorsements" */ "~/components/allEndorsements.vue"),
	},
	async asyncData(context) {
		await context.store.dispatch("reviews/fetchCompany")
	},
	metaInfo() {
		return {
			title: "About Us - Car Rental throughout NZ - GO Rentals",
			meta: [
				{
					vmid: "description",
					name: "description",
					content: "Find out more about the outstanding service & quality range of hire cars at GO Rentals. Our award-winning & friendly team will kick off your NZ adventure.",
				},
				{ vmid: "ogtitle", property: "og:title", content: "About Us - Car Rental throughout NZ - GO Rentals" },
				{ vmid: "ogurl", property: "og:url", content: "https://www.gorentals.co.nz/about-us/why-choose-go/" },
			],
			link: [{ rel: "canonical", href: "https://www.gorentals.co.nz/about-us/why-choose-go/" }],
		}
	},
	computed: {
		pageHeader() {
			return {
				title: "Why GO with us?",
				headline: "We're a different kind of car rental company. <br class='hidden tablet:unset' />Find out what makes us tick.",
				breadcrumb: { theme: "light", crumbs: [{ href: "/", text: "Home" }, { text: "About Us" }, { text: "Why GO with us?" }] },
				picture: {
					alt: "Image of a Toyota Rav4 at dusk overlooking the lakes and mountains of the South Island",
					mobile: { src: "/images/About_Hero_Mobile.jpg", width: 375, height: 495 },
					tablet: { src: "/images/About_Hero_Tablet.jpg", width: 720, height: 720  },
					tabletWide: { src: "/images/About_Hero_Tablet_Wide.jpg", width: 976, height: 696 },
					desktop: { src: "/images/About_Hero_Desktop.jpg", width: 1384, height: 718 },
					desktopWide: { src: "/images/About_Hero_Desktop_Wide.jpg", width: 1864, height: 718 },
				},
			}
		},
	}
};
</script>
